import type { FC } from 'react';
import { useContext } from 'react';
import pipeSource from '@epimodev/callbag-doki/utils/pipe';
import scan from '@epimodev/callbag-doki/operators/scan';

import ConfigContext from 'app/common/ConfigContext';
import ThemeContext from 'app/common/ThemeContext';
import { loadAggregatedMatchData } from 'app/utils/api';
import { mergeAggregatedMatchData } from 'app/utils/entities/match';
import { formatGoldLead } from 'app/utils/entities/match_data';
import { useSubscription } from 'app/hooks/useSubscription';
import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';

import MatchName from './MatchName';
import ScoreCard from './ScoreCard';
import TeamInfos from './TeamInfos';
import EconomyRow from './EconomyRow';
import Dota2DetailedSection from './Dota2DetailedSection';
import CsDetailedSection from './CsDetailedSection';
import {
  countGamesWon,
  getCurrentAggregatedGame,
  getCurrentOpponentSide,
  getFinalDota2GoldAdvantage,
  getSideColorsStyle,
  hasField
} from './utils';
import style from './style.module.scss';

interface Props {
  data: NameBlockMatch;
}

const NameBlockGrad: FC<Props> = ({ data }) => {
  const { customer } = useContext(ConfigContext);
  const { theme } = useContext(ThemeContext);

  const { data: liveMatch } = useSubscription<AggregatedMatch | null>(
    () =>
      pipeSource(
        loadAggregatedMatchData(data.id, customer),
        scan(
          (acc, message) => mergeAggregatedMatchData(acc, message),
          null as AggregatedMatch | null
        )
      ),
    [],
    { prevent: data.match_status === 'finished' }
  );

  const opponent1 = data.opponents.at(0);
  const opponent2 = data.opponents.at(1);
  const isDota2 = data.videogame_slug === 'dota-2';
  const isCs = data.videogame_slug === 'cs-go';

  const matchStatus = liveMatch?.match_status ?? data.match_status;

  // Series score (games won) — prefer the live model, fall back to the composed skeleton.
  const scoreGames = liveMatch?.games ?? data.games;
  const opponent1Wins = countGamesWon(scoreGames, opponent1?.id);
  const opponent2Wins = countGamesWon(scoreGames, opponent2?.id);

  const currentGame = liveMatch ? getCurrentAggregatedGame(liveMatch.games) : undefined;
  const dota2Game = isDota2 && currentGame ? (currentGame as AggregatedGameDota2) : undefined;
  const covered = Boolean(liveMatch?.stats_coverage) && matchStatus === 'running';
  const team1 = dota2Game?.teams.find(team => team.id === opponent1?.id);
  const team2 = dota2Game?.teams.find(team => team.id === opponent2?.id);
  // dota2 live stats (economy + draft) are shown while running and covered.
  const hasLiveStats = Boolean(covered && dota2Game && team1 && team2);

  // cs-go: the same coverage gate, but the section also needs the whole series
  // (the map row is one card per game), so the games list is narrowed too.
  const csGame = isCs && currentGame ? (currentGame as AggregatedGameCs) : undefined;
  const csGames = isCs && liveMatch ? (liveMatch as AggregatedMatchCs).games : [];
  const csTeam1 = csGame?.teams.find(team => team.id === opponent1?.id);
  const csTeam2 = csGame?.teams.find(team => team.id === opponent2?.id);
  const hasCsLiveStats = Boolean(covered && csGame);

  // The CT/T tag is the cs-go counterpart of the radiant/dire tag, gated on the
  // game advertising `side` rather than on coverage — a basic game carries it too.
  const csShowSide = Boolean(csGame && hasField(csGame.available_fields, 'side'));
  const team1Side = team1?.side ?? (csShowSide ? csTeam1?.side : undefined);
  const team2Side = team2?.side ?? (csShowSide ? csTeam2?.side : undefined);

  // Once opponent 1's side is known (live feed first, else the match skeleton: lol has
  // no live sides), this block's team colors take the sides' colors, as CSS variables
  // scoped to the block's section.
  const opponent1Side = team1Side ?? getCurrentOpponentSide(data.games, opponent1?.id);
  const sideColorsStyle = getSideColorsStyle(theme, opponent1Side);

  // Gold lead (+Xk) sits on whichever team currently leads (by side).
  const [goldValue, goldSide] = getFinalDota2GoldAdvantage(dota2Game?.radiant_gold_adv);
  const getGoldLeadIndex = (): 0 | 1 | null => {
    if (!goldSide || !team1 || !team2) return null;
    if (team1.side === goldSide) return 0;
    if (team2.side === goldSide) return 1;
    return null;
  };

  // Compact (short) card for pre-match and finished; full card while running.
  const compact = matchStatus !== 'running';

  // Inter-game break: a game in the series just finished but the match is still
  // running (waiting for the next game). Show the winner + "Break" overlay on
  // top of the live stats. Match-end (match_status finished) keeps the compact
  // finished card, not this break state.
  const isBreak = matchStatus === 'running' && currentGame?.status === 'finished';
  const getBreakWinnerIndex = (): 0 | 1 | null => {
    const winnerId = isBreak ? currentGame?.winner_id : null;
    if (!winnerId) return null;
    if (winnerId === opponent1?.id) return 0;
    if (winnerId === opponent2?.id) return 1;
    return null;
  };
  const breakWinnerIndex = getBreakWinnerIndex();
  const breakWinner =
    breakWinnerIndex !== null ? [opponent1, opponent2][breakWinnerIndex] : undefined;

  return (
    <BlockGradTemplate
      hasData={data.opponents.length > 0}
      contentClassName={style.nameBlockGrad}
      style={sideColorsStyle}
    >
      <MatchName data={data} isLive={matchStatus === 'running'} />
      <ScoreCard hasEconomy={hasLiveStats}>
        <TeamInfos
          data={data}
          opponent1={opponent1}
          opponent2={opponent2}
          matchStatus={matchStatus}
          opponent1Wins={opponent1Wins}
          opponent2Wins={opponent2Wins}
          currentGamePosition={currentGame?.position}
          team1Side={team1Side}
          team2Side={team2Side}
          goldLeadIndex={getGoldLeadIndex()}
          goldLeadLabel={
            goldValue !== undefined ? formatGoldLead(goldValue, 'abbreviated', true) : null
          }
          compact={compact}
        />
        {hasLiveStats && dota2Game && team1 && team2 && (
          <>
            <div className={style.divider} />
            <EconomyRow
              team1={team1}
              team2={team2}
              availableFields={dota2Game.available_fields}
              timer={dota2Game.timer}
            />
          </>
        )}
      </ScoreCard>
      {hasLiveStats && dota2Game && team1 && team2 && (
        <Dota2DetailedSection
          availableFields={dota2Game.available_fields}
          game={dota2Game}
          team1={team1}
          team2={team2}
          opponent1={opponent1}
          opponent2={opponent2}
          breakWinner={breakWinner}
          breakWinnerIndex={breakWinnerIndex}
        />
      )}
      {hasCsLiveStats && csGame && (
        <CsDetailedSection
          availableFields={csGame.available_fields}
          game={csGame}
          games={csGames}
          team1={csTeam1}
          team2={csTeam2}
          opponent1={opponent1}
          opponent2={opponent2}
          breakWinner={breakWinner}
          breakWinnerIndex={breakWinnerIndex}
        />
      )}
    </BlockGradTemplate>
  );
};

NameBlockGrad.displayName = 'NameBlockGrad';

export default NameBlockGrad;
